import { ConfirmDialog } from "@/components/common/ConfirmDialog/ConfirmDialog"
import { DropOverlay } from "@/components/common/DropOverlay/DropOverlay"
import { EmptyState } from "@/components/common/EmptyState/EmptyState"
import { FileUploadIllustration } from "@/components/common/FileUploadIllustration/FileUploadIllustration"
import { toast } from "@/components/common/Toast"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { PERMISSIONS } from "@/constants/permissions"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useRegisterAgentContext } from "@/features/agent-chat/context/AgentPageContext"
import { HelpdeskLink } from "@/features/helpdesk"
import { isPermissionDenied } from "@/hooks/useGuardedMutation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { IconLoader2, IconTrash } from "@tabler/icons-react"
import {
  Check,
  FolderOpen,
  MoreVertical,
  RotateCcw,
  Trash2,
} from "lucide-react"
import { useRef, useState } from "react"
import { useNavigate } from "react-router"
import { toastApiError } from "../../api/apiError"
import {
  useDeleteImportFile,
  useImportHistory,
  useRetryAnalysis,
  useUploadImportFile,
  type ImportFile,
  type ImportHistoryItem,
} from "../../api/importsApi"
import { ENTITY_META, type EntityGroupType } from "../../api/ingestionApi"
import { intlLocale } from "../../utils/date"
import { importStatusDisplay } from "../../utils/importStatus"
import { customerRequestImportPath } from "../customer-requests/routes"
import { McPill } from "../shared/McPill"
import { McTable, McTd, McTr } from "../shared/McTable"
import { SectionLabel } from "../shared/SectionLabel"
import { ErpImportPanel } from "./ErpImportPanel"

// ─── Upload illustration ───────────────────────────────────────────────────────

const IMPORT_FORMATS: [
  { label: string; colorClass: string },
  { label: string; colorClass: string },
] = [
  { label: "XLSX", colorClass: "text-success" },
  { label: "CSV", colorClass: "text-grey5" },
]

// ─── Import history row ────────────────────────────────────────────────────────

const PROCESSING_STATUSES: ImportHistoryItem["status"][] = [
  "uploaded",
  "parsing",
  "extracting",
]
const MAPPING_STATUSES: ImportHistoryItem["status"][] = [
  "mapping-ready",
  "mapping-confirmed",
]

function ImportHistoryRow({
  record,
  onNavigate,
  onRequestDelete,
}: {
  record: ImportHistoryItem
  onNavigate: (url: string) => void
  onRequestDelete: (record: ImportHistoryItem) => void
}) {
  const { translate, currentLanguage } = useTranslation()

  function handleClick() {
    const params = new URLSearchParams([["file_ids", record.id]])
    if (PROCESSING_STATUSES.includes(record.status)) {
      onNavigate(`/material-compliance/master-data/processing?${params}`)
    } else if (MAPPING_STATUSES.includes(record.status)) {
      onNavigate(`/material-compliance/master-data/mapping?${params}`)
    }
  }

  const isClickable =
    PROCESSING_STATUSES.includes(record.status) ||
    MAPPING_STATUSES.includes(record.status)

  const { key: statusKey, tone: statusTone } = importStatusDisplay(record)
  const statusPill = (
    <McPill
      tone={statusTone}
      icon={record.status === "committed" ? Check : undefined}
    >
      {translate(statusKey)}
    </McPill>
  )

  const entitySummary = record.entitiesToWrite
    ? Object.entries(record.entitiesToWrite)
        .map(([type, count]) => {
          const labelKey = ENTITY_META[type as EntityGroupType]?.labelKey
          const label = labelKey ? translate(labelKey) : type
          return `${count.toLocaleString(intlLocale(currentLanguage))} ${label}`
        })
        .join(" · ")
    : "—"

  const ext =
    record.filename.split(".").pop()?.toUpperCase() ??
    translate("MC_MD_IMPORT_FILE_EXT_FALLBACK")
  const date = record.createdAt
    ? new Date(record.createdAt).toLocaleDateString(intlLocale(currentLanguage))
    : "—"

  // Not while committed (data is live) or committing (worker mid-transaction —
  // deleting would wipe the staging record the moment the commit lands).
  // Also gate on MC_IMPORTS_DELETE permission.
  const { hasPermission } = useUser()
  const canDelete =
    hasPermission(PERMISSIONS.MC_IMPORTS_DELETE) &&
    record.status !== "committed" &&
    record.status !== "committing"
  const canRetry =
    hasPermission(PERMISSIONS.MC_IMPORTS_UPLOAD) && !!record.analysisError

  const retryAnalysis = useRetryAnalysis()
  function handleRetry() {
    retryAnalysis.mutate(record.id, {
      onSuccess: () =>
        onNavigate(
          `/material-compliance/master-data/processing?${new URLSearchParams([["file_ids", record.id]])}`,
        ),
      onError: (err) =>
        toastApiError(err, translate, "MC_MD_IMPORT_RETRY_ERROR"),
    })
  }

  return (
    <McTr onClick={isClickable ? handleClick : undefined}>
      <McTd variant="accent" mono>
        {record.importNumber ?? record.id.slice(0, 8)}
      </McTd>
      <McTd variant="primary" className="max-w-[240px]">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="font-mono shrink-0 text-[9.5px]">
            {ext}
          </Badge>
          <span className="truncate">{record.filename}</span>
        </div>
      </McTd>
      <McTd variant="muted" mono className="whitespace-nowrap">
        {date}
      </McTd>
      <McTd variant="muted">{record.createdByEmail ?? "—"}</McTd>
      <McTd variant="muted">
        {entitySummary === "—" ? (
          "—"
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="font-mono cursor-default text-[11.5px] text-white underline decoration-dotted underline-offset-4">
                {record.totalRecords.toLocaleString(
                  intlLocale(currentLanguage),
                )}
              </span>
            </TooltipTrigger>
            <TooltipContent>{entitySummary}</TooltipContent>
          </Tooltip>
        )}
      </McTd>
      <McTd>
        {record.analysisError ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="cursor-default">
                {statusPill}
              </span>
            </TooltipTrigger>
            <TooltipContent>{record.analysisError}</TooltipContent>
          </Tooltip>
        ) : (
          statusPill
        )}
      </McTd>
      <McTd className="text-right whitespace-nowrap">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-grey5"
              disabled={!canDelete && !canRetry}
              aria-label={translate("MC_MD_IMPORT_MORE_ACTIONS")}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical size={15} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            {canRetry && (
              <DropdownMenuItem
                disabled={retryAnalysis.isPending}
                onSelect={handleRetry}
              >
                <RotateCcw size={14} />
                {translate("MC_MD_IMPORT_RETRY")}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              className="text-red1 focus:text-red1"
              disabled={!canDelete}
              onSelect={() => onRequestDelete(record)}
            >
              <Trash2 size={14} />
              {translate("MC_MD_IMPORT_MENU_REMOVE")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </McTd>
    </McTr>
  )
}

// ─── Root module ───────────────────────────────────────────────────────────────

export function MasterDataImportModule() {
  const navigate = useNavigate()
  const { translate } = useTranslation()
  const showErp = useFeatureFlag("MC_ERP_IMPORT")
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [isUploading, setIsUploading] = useState(false)
  const [rowToDelete, setRowToDelete] = useState<ImportHistoryItem | null>(null)

  const { mutateAsync: uploadFile } = useUploadImportFile()
  const { hasPermission } = useUser()
  const canUpload = hasPermission(PERMISSIONS.MC_IMPORTS_UPLOAD)
  const { data: history = [], isLoading: historyLoading } = useImportHistory()

  // Lets "why did this one fail?" resolve to a file without the user pasting an id.
  useRegisterAgentContext({
    route: "/material-compliance/master-data/import",
    entities: {
      mc_entity: "import",
      import_file_ids: history.map((r) => r.id),
    },
  })

  const deleteImportFile = useDeleteImportFile()

  // Upload real File objects — one POST /files per file, in parallel. We wait
  // for every upload to settle (success or failure), then move on to the
  // processing page if at least one landed. Analysis starts automatically
  // server-side (upload responds with status "parsing"), so the processing
  // page only needs to poll.
  async function handleFiles(files: File[]) {
    if (!files.length || isUploading) return

    setIsUploading(true)

    const results = await Promise.allSettled(
      files.map((file) => uploadFile(file)),
    )

    setIsUploading(false)

    const fulfilledResults = results.filter(
      (r): r is PromiseFulfilledResult<ImportFile> => r.status === "fulfilled",
    )
    const succeeded = fulfilledResults.length
    // Permission-denied rejections already produced the guard's translated
    // toast — skip them from the failure count so we don't double up with
    // "Upload failed" on top.
    const failed = results.filter(
      (r) => r.status === "rejected" && !isPermissionDenied(r.reason),
    ).length

    if (failed > 0) {
      toast.error(
        succeeded > 0
          ? translate("MC_MD_IMPORT_ERROR_PARTIAL", {
              failed,
              total: results.length,
            })
          : translate("MC_MD_IMPORT_ERROR_ALL"),
      )
    }
    if (succeeded > 0) {
      const uploadedIds = fulfilledResults.map((r) => r.value.id)
      const params = new URLSearchParams(
        uploadedIds.map((id) => ["file_ids", id]),
      )
      navigate(`/material-compliance/master-data/processing?${params}`)
    }
  }

  function pickFromDisk(fileList: FileList | null) {
    handleFiles(Array.from(fileList ?? []))
  }

  function handleConfirmDelete() {
    if (!rowToDelete) return
    deleteImportFile.mutate(rowToDelete.id, {
      onSuccess: () => {
        toast.success(translate("MC_MD_IMPORT_REMOVE_SUCCESS"))
        setRowToDelete(null)
      },
      onError: (err) => {
        toastApiError(err, translate, "MC_MD_IMPORT_REMOVE_ERROR")
      },
    })
  }

  const uploadPanel = (
    <EmptyState
      illustration={<FileUploadIllustration formats={IMPORT_FORMATS} />}
      title={translate("MC_MD_IMPORT_DROP_TITLE")}
      description={
        <>
          {translate("MC_MD_IMPORT_DROP_DESC")}
          <span className="mt-2 block text-grey5">
            {translate("MC_IMPORT_ELSEWHERE_REQ")}{" "}
            <button
              type="button"
              className="font-medium text-yellow1 hover:underline"
              onClick={() => navigate(customerRequestImportPath)}
            >
              {translate("MC_IMPORT_ELSEWHERE_REQ_LINK")}
            </button>
          </span>
        </>
      }
      hoverable
      className="h-auto md:min-h-[480px]"
      onClose={() => navigate("/material-compliance/master-data")}
      primaryAction={
        canUpload
          ? {
              label: isUploading
                ? translate("MC_MD_IMPORT_UPLOADING")
                : translate("MC_MD_IMPORT_CHOOSE_FILES"),
              icon: isUploading ? (
                <IconLoader2 size={14} className="animate-spin" />
              ) : (
                <FolderOpen size={14} />
              ),
              onClick: () => fileInputRef.current?.click(),
              disabled: isUploading,
            }
          : undefined
      }
    />
  )

  return (
    <TooltipProvider delayDuration={200}>
      <div
        data-agent-target="import.master-data"
        className="flex min-h-full flex-col gap-5 p-6"
      >
        <HelpdeskLink
          categoryId="master-data"
          articleId="master-data.import"
          className="self-end"
        />
        {/* Only wrap in tabs when the ERP import tab is enabled — otherwise a
            lone "Upload" tab is pointless, so show the upload panel directly. */}
        {showErp ? (
          <Tabs defaultValue="upload">
            <TabsList>
              <TabsTrigger value="upload">
                {translate("MC_MD_IMPORT_TAB_UPLOAD")}
              </TabsTrigger>
              <TabsTrigger value="erp">
                {translate("MC_MD_IMPORT_TAB_ERP")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="upload">{uploadPanel}</TabsContent>
            <TabsContent value="erp">
              <ErpImportPanel />
            </TabsContent>
          </Tabs>
        ) : (
          uploadPanel
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xlsm,.csv,.xls"
          multiple
          className="hidden"
          onChange={(e) => {
            pickFromDisk(e.target.files)
            e.target.value = ""
          }}
        />

        <div data-agent-target="import.history">
          <SectionLabel className="mb-3">
            {translate("MC_MD_IMPORT_HISTORY_TITLE")}
          </SectionLabel>
          <McTable
            headers={[
              translate("MC_MD_IMPORT_HISTORY_COL_ID"),
              translate("MC_MD_IMPORT_HISTORY_COL_FILE"),
              translate("MC_MD_IMPORT_HISTORY_COL_DATE"),
              translate("MC_MD_IMPORT_HISTORY_COL_BY"),
              translate("MC_MD_IMPORT_HISTORY_COL_ENTITIES"),
              translate("MC_MD_IMPORT_HISTORY_COL_STATUS"),
              "",
            ]}
          >
            <tbody>
              {historyLoading
                ? Array.from({ length: 4 }).map((_, i) => (
                    <McTr key={i}>
                      <McTd>
                        <Skeleton className="h-3.5 w-16" />
                      </McTd>
                      <McTd>
                        <Skeleton className="h-3.5 w-40" />
                      </McTd>
                      <McTd>
                        <Skeleton className="h-3.5 w-20" />
                      </McTd>
                      <McTd>
                        <Skeleton className="h-3.5 w-28" />
                      </McTd>
                      <McTd>
                        <Skeleton className="h-3.5 w-48" />
                      </McTd>
                      <McTd>
                        <Skeleton className="h-3.5 w-16 rounded-full" />
                      </McTd>
                      <McTd>
                        <Skeleton className="h-3.5 w-7" />
                      </McTd>
                    </McTr>
                  ))
                : history.map((record) => (
                    <ImportHistoryRow
                      key={record.id}
                      record={record}
                      onNavigate={navigate}
                      onRequestDelete={setRowToDelete}
                    />
                  ))}
            </tbody>
          </McTable>
        </div>
      </div>

      {canUpload && (
        <DropOverlay
          acceptedExtensions={[".xlsx", ".xls", ".xlsm", ".csv"]}
          hint={translate("MC_MD_IMPORT_DROP_OVERLAY_HINT")}
          onFilesDropped={(files) => handleFiles(Array.from(files))}
        />
      )}

      {rowToDelete && (
        <ConfirmDialog
          variant="danger"
          icon={IconTrash}
          title={translate("MC_MD_IMPORT_REMOVE_TITLE")}
          message={translate("MC_MD_IMPORT_REMOVE_DESC")}
          confirmLabel={translate("MC_MD_IMPORT_REMOVE_CONFIRM")}
          cancelLabel={translate("MC_MD_IMPORT_REMOVE_CANCEL")}
          isLoading={deleteImportFile.isPending}
          onConfirm={handleConfirmDelete}
          onCancel={() => setRowToDelete(null)}
        />
      )}
    </TooltipProvider>
  )
}
